import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, useTheme, Tooltip, Button, alpha } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Grid from "@mui/material/Grid";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { useReportFilters } from "./hooks/useReportFilters";
import { useReportsData } from "./hooks/useReportsData";
import { usePageHeader } from "../../context/LayoutContext";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { ReportFilters } from "./components/ReportFilters";
import { SortControls } from "./components/SortControls";
import { ReportsPagination } from "./components/ReportsPagination";
import { EmptyReportsState } from "./components/EmptyReportsState";
import { ReportCard } from "./components/ReportCard";
import { ReportCardSkeleton } from "./components/ReportCardSkeleton";

import { BodycamContext } from "../../context/BodycamContext";
import ConfirmationModal from "../../modals/ConfirmationModal";
import InactivityRedirectModal from "../../modals/InactivityRedirectModal";
import { SnackBarMessage } from "../../modals/SnackBarMessage";
import { useErrorSnackbar } from "../../shared/hooks/useErrorSnackbar";
import { useSnackbarPosition } from "../../shared/hooks/useSnackbarPosition";
import ErrorAlert from "../../components/feedback/ErrorAlert";
import JobMonitor from "../../components/jobs/JobMonitor";
import { JobRowSkeleton } from "../../components/skeletons/JobRowSkeleton";
import { useJobStore } from "../../store/jobStore";
import { ReportJobStatus } from "../../shared/interfaces/JobStatus";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";

// Enable dayjs plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const HomePage: React.FC = () => {
  const theme = useTheme();
  const vars = theme;
  const navigate = useNavigate();
  const location = useLocation();
  const { stationOfficers: stationOfficersState } =
    React.useContext(BodycamContext);
  const [stationOfficers] = stationOfficersState;

  // Hooks
  const {
    snackBarMessage,
    snackBarMode,
    showSuccess,
    handleCloseSnackbar,
  } = useErrorSnackbar();
  const snackbarPosition = useSnackbarPosition();

  const [page, setPage] = useState(0);
  const [fetchError, setFetchError] = useState<string>("");
  const filters = useReportFilters(setPage);

  const showFetchError = useCallback(
    (message: string) => {
      setFetchError(message);
    },
    [],
  );

  const {
    reports,
    totalReports,
    isLoading,
    rowsPerPage,
    setRowsPerPage,
    sortConfig,
    requestSort,
    openDeleteModal,
    reportToDelete,
    handleReportDelete,
    handleModalClose,
    handleOpenDeleteModal,
    fetchReports,
  } = useReportsData(filters, page, showFetchError, showSuccess);

  // Inactivity Modal State
  const [inactivityModalOpen, setInactivityModalOpen] = useState(false);
  const [inactivityMessage, setInactivityMessage] = useState<string>("");

  const jobs = useJobStore((state) => state.jobs);
  const pendingJobs = jobs.filter(
    (job) => job.status === ReportJobStatus.PENDING,
  );

  // Computed data for filters
  const availableReporters = useMemo(() => {
    if (!stationOfficers) return [];
    return stationOfficers
      .map((officer) => ({
        name: `${officer.firstName} ${officer.lastName}`,
        id: officer.id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [stationOfficers]);

  const availableModifiers = useMemo(() => {
    if (!stationOfficers) return [];
    return stationOfficers
      .map((officer) => ({
        name: `${officer.firstName} ${officer.lastName}`,
        id: officer.id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [stationOfficers]);

  const allReportStatuses = useMemo(() => {
    return (Object.values(ReportStatus) as ReportStatus[])
      .filter((v) => typeof v === "string")
      .sort((a, b) => (a || "").toString().localeCompare((b || "").toString()));
  }, []);

  // Effects
  useEffect(() => {
    const state =
      (location.state as { message?: string } | undefined) || undefined;
    if (state?.message) {
      setInactivityMessage(state.message);
      setInactivityModalOpen(true);
      navigate(location.pathname, { replace: true, state: undefined });
    }
  }, [location.state, location.pathname, navigate]);

  // Reset page when filters change
  useEffect(() => {
    if (setPage) {
      // Guard in case useReportsData hasn't been updated yet
      setPage(0);
    }
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.reportedDateFrom,
    filters.reportedDateTo,
    filters.debouncedDetailsFilter,
    filters.reportedByFilter,
    filters.modifiedByFilter,
    filters.selectedStatuses,
    setPage,
  ]);

  const hasActiveFilters =
    filters.dateFrom !== null ||
    filters.dateTo !== null ||
    filters.reportedDateFrom !== null ||
    filters.reportedDateTo !== null ||
    filters.debouncedDetailsFilter.trim() !== "" ||
    filters.reportedByFilter.length > 0 ||
    filters.modifiedByFilter.length > 0 ||
    filters.selectedStatuses.length > 0;

  // Set page header in app bar
  usePageHeader({
    title: "My Reports",
    subtitle: "View, filter, and manage your incident reports",
    icon: <AssessmentIcon />,
    chip: { label: `${totalReports} Reports` },
  });

  return (
    <Container sx={{ overflow: "auto" }}>
      <JobMonitor />

      <ConfirmationModal
        open={openDeleteModal}
        dialogTitle={`Are You Sure You Want to Delete ${reportToDelete?.reportName}?`}
        dialogMessage="This action cannot be undone."
        onClose={handleModalClose}
        onConfirm={handleReportDelete}
      />

      <InactivityRedirectModal
        open={inactivityModalOpen}
        onAcknowledge={() => setInactivityModalOpen(false)}
        message={inactivityMessage}
      />

      <SnackBarMessage
        handleClose={handleCloseSnackbar}
        message={snackBarMessage}
        mode={snackBarMode}
        horizontalAnchor="center"
        verticalAnchor="top"
        sxOverrides={snackbarPosition}
      />

      <Grid
        container
        spacing={3}
        direction="column"
        sx={{ display: "flex", alignItems: "stretch", py: 2 }}
      >
        <Grid size={{ xs: 12 }}>
          <ReportFilters
            filters={filters}
            availableReporters={availableReporters}
            availableModifiers={availableModifiers}
            allReportStatuses={allReportStatuses}
          />
        </Grid>

        <Grid
          size={{ xs: 12 }}
          container
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Grid size={{ xs: 12, md: "auto" }} sx={{ overflow: "hidden" }}>
            <SortControls sortConfig={sortConfig} onSortChange={requestSort} />
          </Grid>
          <Grid size={{ xs: 12, md: "auto" }}>
            <Tooltip title="Create a new incident report" arrow>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/video-selection")}
                sx={{
                  height: 48,
                  px: 4,
                  textTransform: "none",
                  borderRadius: 2,
                  fontSize: "1rem",
                  fontWeight: 600,
                  width: { xs: "100%", md: "auto" },
                  color: "#fff",
                  background: `linear-gradient(135deg, ${vars.palette.primary.main} 0%, ${vars.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px 0 ${alpha(
                    vars.palette.primary.main,
                    0.4,
                  )}`,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 20px 0 ${alpha(
                      vars.palette.primary.main,
                      0.5,
                    )}`,
                    background: `linear-gradient(135deg, ${vars.palette.primary.light} 0%, ${vars.palette.primary.main} 100%)`,
                  },
                  "&:active": {
                    transform: "translateY(0)",
                    boxShadow: `0 2px 8px 0 ${alpha(
                      vars.palette.primary.main,
                      0.4,
                    )}`,
                  },
                  "&:focus-visible": {
                    outline: `3px solid ${alpha(
                      vars.palette.primary.main,
                      0.5,
                    )}`,
                    outlineOffset: 2,
                  },
                }}
              >
                New Report
              </Button>
            </Tooltip>
          </Grid>
        </Grid>

        <Grid size={{ xs: 12 }}>
          {fetchError && (
            <ErrorAlert
              message={fetchError}
              onRetry={() => {
                setFetchError("");
                fetchReports();
              }}
            />
          )}

          <Grid container spacing={2}>
            {isLoading ? (
              Array.from(new Array(rowsPerPage)).map((_, index) => (
                <Grid size={{ xs: 12 }} key={index}>
                  <ReportCardSkeleton />
                </Grid>
              ))
            ) : (
              <>
                {pendingJobs.map((job) => (
                  <Grid size={{ xs: 12 }} key={`job-${job.id}`}>
                    <JobRowSkeleton
                      reportName={
                        job.reportName ||
                        `Report from ${job.evidenceIDs.length} video(s)`
                      }
                    />
                  </Grid>
                ))}

                {reports.length > 0 ? (
                  reports.map((report) => (
                    <Grid size={{ xs: 12 }} key={report.id}>
                      <ReportCard
                        report={report}
                        navigate={navigate}
                        onDelete={handleOpenDeleteModal}
                      />
                    </Grid>
                  ))
                ) : pendingJobs.length === 0 ? (
                  <Grid size={{ xs: 12 }}>
                    <EmptyReportsState
                      hasActiveFilters={hasActiveFilters}
                      onResetFilters={filters.handleResetFilters}
                    />
                  </Grid>
                ) : null}
              </>
            )}
          </Grid>

          <ReportsPagination
            page={page}
            totalReports={totalReports}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default HomePage;
